import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, isBefore, startOfDay } from 'date-fns';

const CalendarDropdown = ({
  selectedDate,
  onDateChange,
  value,
  onChange,
  placeholder = "Select date",
  minDate = new Date(),
  maxDate = null,
  className = "",
  disabled = false,
  error = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const displayDate = value || selectedDate;
  const [currentMonth, setCurrentMonth] = useState(displayDate || new Date());
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDateSelect = (date) => {
    const finalDate = startOfDay(date);
    if (onChange) {
      onChange(finalDate);
    } else if (onDateChange) {
      onDateChange(finalDate);
    }
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (onChange) {
      onChange(null);
    } else if (onDateChange) {
      onDateChange(null);
    }
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(prev => subMonths(prev, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => addMonths(prev, 1));
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  };

  const isDateDisabled = (date) => {
    if (minDate && isBefore(date, startOfDay(minDate))) return true;
    if (maxDate && isBefore(startOfDay(maxDate), date)) return true;
    return false;
  };

  const getDayClasses = (date) => {
    const baseClasses = "w-8 h-8 flex items-center justify-center text-sm rounded-full cursor-pointer transition-colors";
    
    if (isDateDisabled(date)) {
      return `${baseClasses} text-gray-300 cursor-not-allowed`;
    }
    
    if (displayDate && isSameDay(date, displayDate)) {
      return `${baseClasses} bg-primary-600 text-white`;
    }
    
    if (isToday(date)) {
      return `${baseClasses} bg-primary-100 text-primary-700 hover:bg-primary-200`;
    }
    
    return `${baseClasses} text-gray-700 hover:bg-gray-100`;
  };

  const renderCalendarHeader = () => (
    <div className="flex items-center justify-between p-3 border-b border-gray-200">
      <button
        onClick={goToPreviousMonth}
        className="p-1 hover:bg-gray-100 rounded-md transition-colors"
        disabled={minDate && isBefore(subMonths(currentMonth, 1), startOfMonth(minDate))}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      
      <h3 className="text-sm font-semibold text-gray-900">
        {format(currentMonth, 'MMMM yyyy')}
      </h3>
      
      <button
        onClick={goToNextMonth}
        className="p-1 hover:bg-gray-100 rounded-md transition-colors"
        disabled={maxDate && isBefore(startOfMonth(maxDate), addMonths(currentMonth, 1))}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );

  const renderCalendarDays = () => {
    const days = getDaysInMonth();
    const startOfWeek = new Date(days[0]);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    
    const calendarDays = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      calendarDays.push(date);
    }

    return (
      <div className="p-3">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-xs text-gray-500 text-center py-1">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((date, index) => {
            const isCurrentMonth = isSameMonth(date, currentMonth);
            const isDisabled = isDateDisabled(date);
            
            return (
              <button
                key={index}
                onClick={() => !isDisabled && handleDateSelect(date)}
                disabled={isDisabled}
                className={`${getDayClasses(date)} ${
                  !isCurrentMonth ? 'text-gray-300' : ''
                }`}
              >
                {format(date, 'd')}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Input field */}
      <div className="relative">
        <input
          type="text"
          value={displayDate ? format(displayDate, 'dd/MM/yyyy') : ''}
          placeholder={placeholder}
          readOnly
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`w-full px-3 py-2 border rounded-md pr-10 cursor-pointer transition-colors ${
            disabled 
              ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
              : 'bg-white hover:border-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500'
          } ${error ? 'border-red-500' : 'border-gray-300'}`}
          disabled={disabled}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {displayDate && !disabled ? (
            <button
              onClick={(e) => handleClear(e)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          ) : (
            <Calendar className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg">
          {renderCalendarHeader()}
          {renderCalendarDays()}
        </div>
      )}
    </div>
  );
};

export default CalendarDropdown; 